import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { CreditCard, ArrowLeft, Shield } from 'lucide-react';
import { i18n } from '../utils/i18n';

interface RegisterProps {
  onRegister: (nickname: string, pin: string, phoneNumber?: string, fullName?: string) => Promise<{ success: boolean; message: string; hasReferral?: boolean }>;
  onSwitchToLogin: () => void;
  isLoading: boolean;
}

export function Register({ onRegister, onSwitchToLogin, isLoading }: RegisterProps) {
  const [nickname, setNickname] = useState('');
  const [fullName, setFullName] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showWelcomeBonus, setShowWelcomeBonus] = useState(false);
  const [showPin, setShowPin] = useState(true);
  const [showConfirmPin, setShowConfirmPin] = useState(true);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName.trim()) {
      toast.error('이름을 입력해주세요.');
      return;
    }

    if (!phoneNumber.trim()) {
      toast.error('전화번호를 입력해주세요.');
      return;
    }

    if (!nickname.trim() || !pin.trim() || !confirmPin.trim()) {
      toast.error(i18n.t('pleaseEnterAll'));
      return;
    }

    if (pin.length < 6) {
      toast.error(i18n.t('pinMinLength'));
      return;
    }

    if (pin !== confirmPin) {
      toast.error(i18n.t('pinsDoNotMatch'));
      return;
    }

    // 전화번호 유효성 검사 (선택사항)
    if (phoneNumber && !/^\d{10,11}$/.test(phoneNumber.replace(/[^0-9]/g, ''))) {
      toast.error('Please enter a valid phone number.');
      return;
    }

    try {
      const result = await onRegister(nickname, pin, phoneNumber, fullName);
      if (result.success) {
        toast.success(result.message);
        // 회원가입 성공 시 메인으로 이동 (추천코드와 관계없이)
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('registrationFailed'));
    }
  };



  return (
    <div className="h-full bg-background flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSwitchToLogin}
          className="text-muted-foreground p-0 hover:text-foreground hover:bg-transparent"
        >
          <ArrowLeft size={24} />
        </Button>
        <h1 className="text-2xl font-light text-foreground">{i18n.t('createAccount')}</h1>
        <div className="w-6"></div>
      </div>

      {/* Logo */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl mb-4">
          <CreditCard className="h-8 w-8 text-white" />
        </div>
        <p className="text-muted-foreground text-base">{i18n.t('joinHandyWallet')}</p>
      </div>

      {/* Form */}
      <div className="flex-1 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="이름 *"
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              maxLength={20}
            />
          </div>

          <div>
            <Input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={i18n.t('usernamePlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={2}
              maxLength={10}
            />
          </div>
          
          <div>
            <Input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={i18n.t('pinPlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={6}
              maxLength={10}
            />
          </div>

          <div>
            <Input
              type="text"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              placeholder={i18n.t('confirmPinPlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={6}
              maxLength={10}
            />
          </div>

          <div>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={i18n.t('phoneNumberPlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
            />
          </div>


          <Button 
            type="submit" 
            className="w-full h-12 mt-8 bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-medium rounded-xl"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              i18n.t('createAccount')
            )}
          </Button>
        </form>

        {/* Security Notice */}
        <Card className="bg-muted border-border mt-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">
                {i18n.t('keepPinSecure')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-6">
          <p className="text-muted-foreground text-sm mb-4">{i18n.t('alreadyHaveAccount')}</p>
          <Button 
            variant="outline"
            onClick={onSwitchToLogin}
            className="w-full h-12 border-border text-foreground bg-transparent hover:bg-muted text-base"
            disabled={isLoading}
          >
            {i18n.t('signIn')}
          </Button>
        </div>
      </div>



      {/* Welcome Bonus Modal - Full Screen */}
      {showWelcomeBonus && (
        <div className="fixed inset-0 bg-gradient-to-br from-green-500 to-emerald-500 flex flex-col items-center justify-center z-50 p-6">
          {/* Animated Wallet Icon */}
          <div className="relative mb-8">
            <div className="w-32 h-32 bg-white rounded-3xl flex items-center justify-center shadow-2xl animate-bounce">
              <CreditCard className="h-16 w-16 text-green-500" />
            </div>
            {/* Floating coins animation */}
            <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-ping">
              <span className="text-sm">💰</span>
            </div>
            <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-yellow-300 rounded-full flex items-center justify-center animate-pulse">
              <span className="text-xs">✨</span>
            </div>
          </div>

          {/* Congratulations Text */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">{i18n.t('congratulations')}</h1>
            <h2 className="text-2xl font-light text-white/90 mb-2">{i18n.t('welcomeToHandy')}</h2>
            <p className="text-white/80 text-lg">{i18n.t('bonusCredited')}</p>
          </div>

          {/* Bonus Amount */}
          <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-8 mb-12 text-center border border-white/30">
            <p className="text-white/80 text-sm mb-2">{i18n.t('welcomeBonusTitle').toUpperCase()}</p>
            <p className="text-6xl font-bold text-white mb-2">100</p>
            <p className="text-white/90 text-xl">wHANDY</p>
          </div>

          {/* Continue Button */}
          <Button
            onClick={() => setShowWelcomeBonus(false)}
            className="w-full max-w-xs h-16 bg-white text-green-600 hover:bg-white/90 font-bold text-xl rounded-2xl shadow-xl"
          >
            {i18n.t('goToWallet')}
          </Button>

          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-4 h-4 bg-white/30 rounded-full animate-bounce delay-100"></div>
          <div className="absolute top-32 right-16 w-3 h-3 bg-white/40 rounded-full animate-bounce delay-200"></div>
          <div className="absolute bottom-32 left-20 w-5 h-5 bg-white/25 rounded-full animate-bounce delay-300"></div>
          <div className="absolute bottom-20 right-24 w-2 h-2 bg-white/50 rounded-full animate-bounce delay-75"></div>
        </div>
      )}
    </div>
  );
}